import { Fragment, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from 'styled-components';

import CarouselDates from '../components/Carousel';
import ExclusiveCreditCardBrand from '../components/ExclusiveCreditCardBrand';
import EmptyState from '../components/NoTickets';
import SummaryMobile from '../components/SummaryMobile';
import BackIcon from '../components/SvgCustom/svg/BackIcon';
import ZoomImageViewer from '../components/ZoomImageViwer';
import { CardsInfo } from '../constants/cards';
import { useBreadCrumbsContext } from '../hooks/contexts/breadCrumb.context';
import { useCartContext } from '../hooks/contexts/cart.context';
import { DataCarouselProps, useEventContext } from '../hooks/contexts/event.context';
import { useI18nContext } from '../hooks/contexts/i18n.context';
import { useModalContext } from '../hooks/contexts/modal.context';
import { ListView, useUiContext } from '../hooks/contexts/ui.context';
import { useUserContext } from '../hooks/contexts/user.context';
import * as Styles from '../styles/pages/tickets';

const TicketsPage = () => {
  const {
    allTicketsUnavailable,
    loading,
    startImage,
    isFootball,
    singleDayDate,
    selectedIdDateCarousel,
    dataCarousel,
    comboList,
  } = useEventContext();
  const { translate } = useI18nContext();
  const { isPaymentPage } = useBreadCrumbsContext();
  const { isMobile, handleRenderListView, listView, handleChangeListView } = useUiContext();
  const { summaryData, combosInCart } = useCartContext();
  const { event, applyPassKey, applyCoupon } = useEventContext();
  const theme = useTheme();
  const [queryParam] = useSearchParams();
  const passkey = queryParam.get('passkey');
  const coupon = queryParam.get('coupon');
  const { modalIsOpen, setContentModal, toggleModal } = useModalContext();
  const { isTicketPage } = useBreadCrumbsContext();
  const [selectedSession, setSelectedSession] = useState<DataCarouselProps>();

  const { renewToken, handleAuthenticatedUser, authenticatedUser } = useUserContext();

  useEffect(() => {
    renewToken();
  }, []);

  const LIST_VIEW_TITLE_DIC = {
    sessions : 'SELECT_SESSION',
    tickets : 'SELECT_SESSION_TICKET',
    ticketsGroups : 'SELECTED_TICKET_GROUPS'
  };

  const showSummaryInMobile = isMobile && (summaryData.length > 0 || combosInCart.length > 0);
  const fullWidthContainer = isPaymentPage || allTicketsUnavailable || isMobile;
  const hasAUniqueSession = isFootball || singleDayDate;
  const saleEnabled = event?.attributes.saleEnabled.value || false;

  useEffect(() => {
    setSelectedSession(dataCarousel.find(({ id }) => id === selectedIdDateCarousel));
  }, [selectedIdDateCarousel]);

  useEffect(() => {
    if (passkey && event?.id && event?.producerId) {
      applyPassKey({ passkey, modalIsOpen, setContentModal, toggleModal });
    }
  }, [passkey, event?.id, event?.producerId]);

  useEffect(() => {
    if (coupon && event?.id && event?.producerId) {
      applyCoupon({ coupon, modalIsOpen, setContentModal, toggleModal });
    }
  }, [coupon, event?.id, event?.producerId]);

  useEffect(() => {
    if (isTicketPage && authenticatedUser) {
      handleAuthenticatedUser(false);
    }
  }, [isTicketPage]);

  const exclusiveCreditCardBrand = event?.attributes.exclusiveCreditCardBrand?.value;

  const hasCombo = comboList.length > 0;
  const renderCombo = hasCombo && selectedSession ? (
    selectedSession.definitiveSessionId === comboList.map((combo) => combo.id).join('')
  ) : false;

  return (
    <Styles.Container
      id="tickets-page"
      $fullWidth={fullWidthContainer}
      $summaryMobileIsOpen={showSummaryInMobile}
    >
      {exclusiveCreditCardBrand && <ExclusiveCreditCardBrand {...CardsInfo[exclusiveCreditCardBrand]} />}
      {
        (!isPaymentPage && !allTicketsUnavailable && saleEnabled ) ? (
          <Fragment>
            { isMobile ? (
              <Styles.WrapperContent $showStartImage={Boolean(startImage)}>
                { startImage && (
                  <Styles.WrapperStartImage id="start-image-wrapper">
                    <Styles.ContainerImage id="start-image-container">
                      <ZoomImageViewer pathImage={startImage.large} />
                    </Styles.ContainerImage>
                  </Styles.WrapperStartImage>
                )}
                <div>
                  { listView === ListView.TICKETS && !hasAUniqueSession && (
                    <Styles.BackToSessionListContainer>
                      <Styles.RedirectText
                        onClick={() => handleChangeListView(ListView.SESSIONS)}
                      >
                        <img src={BackIcon({ colorTheme : theme.colors.accent.primary })} alt="seta voltando" />
                        {translate(['TICKET_TEMPLATE', 'VIEW_SESSIONS'])}
                      </Styles.RedirectText>
                      <Styles.RightContent>
                        <h3>{selectedSession?.title}</h3>
                        <span>{selectedSession?.subtitle}</span>
                      </Styles.RightContent>
                    </Styles.BackToSessionListContainer>
                  )}
                  { listView === ListView.TICKETS && hasAUniqueSession && (
                    <CarouselDates />
                  )}
                  {renderCombo || (
                    <Styles.MobileListTitle id="title-tickets-page-mb">
                      {translate(['TICKET_TEMPLATE', LIST_VIEW_TITLE_DIC[listView]])}
                    </Styles.MobileListTitle>
                  )}
                  { handleRenderListView() }
                </div>
              </Styles.WrapperContent>
            ) : (
              <Fragment>
                <CarouselDates />
                <Styles.WrapperContent $showStartImage={Boolean(startImage)}>
                  { startImage && (
                    <Styles.WrapperStartImage id="start-image-wrapper">
                      <Styles.ContainerImage id="start-image-container">
                        <ZoomImageViewer pathImage={startImage.large} />
                      </Styles.ContainerImage>
                    </Styles.WrapperStartImage>
                  )}
                  { handleRenderListView() }
                </Styles.WrapperContent>
              </Fragment>
            )}
          </Fragment>
        ) : !loading && <EmptyState />
      }
      {showSummaryInMobile && <SummaryMobile />}
    </Styles.Container>
  );
};

export default TicketsPage;
